import React from "react";
import { Drawer, Badge, Tag, Empty, Spin } from "antd";
import {
  IconSchool,
  IconCalendar,
  IconFileText,
  IconUsers,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import useGetApi from "../../hooks/useGetApi";
import leaveApis from "../../api/methods/leaves";
import LeaveDetailsCard from "../common/Card/leaveDetails";

interface LeaveDetailsModalProps {
  leaveId: number | null;
  open: boolean;
  onClose: () => void;
}

export interface TaskDetails {
  id: number;
  student_name: string;
  assignmnet_title: string;
  attachment: string | null;
  assignment_details: string;
  website: string;
  assignment_day: string;
  status: string;
  submission_date: string;
  task_type: string | null;
  duration: number;
  created_at: string;
  district_admin: number;
  student: number;
  grade: number;
  subject: number;
  teacher: number;
  parent: number[];
  leave_request: number;
  leave_request_details: {
    start_date: string;
    end_date: string;
    recovery_method: string;
    requested_date: string;
    is_start_date: string;
    is_end_date: string;
    is_recovered: boolean;
    is_task_completed: boolean;
    submission_date: string | null;
    recovered_days: number;
    dayofabsence: string;
  };
}

export interface LeaveDetailsResponse {
  id: number;
  task?: TaskDetails[];
  student: {
    id: number;
    email: string;
    name: string;
  };
  district_admin: {
    id: number;
    email: string;
    name: string;
  };
  school: {
    id: number;
    email: string;
    name: string;
  };
  parent: Array<{
    id: number;
    email: string;
    name: string;
  }>;
  teacher: Array<{
    id: number;
    email: string;
    name: string;
  }>;
  attendace_coordinator: Array<{
    id: number;
    email: string;
    name: string;
  }>;
  studentid: string;
  student_name: string;
  start_date: string;
  end_date: string;
  selected_dates: string;
  requested_date: string;
  recovery_method: string;
  is_start_date: string;
  is_end_date: string;
  guardian_confirmed: string;
  guardian_number: string | null;
  relation: string;
  reason: string;
  other: string | null;
  status: string;
  total_days: string;
  terms_accepted: boolean;
  tasks_assigned: boolean;
  submission_date: string | null;
  is_recovered: boolean;
  is_task_completed: boolean;
  specialeducationservices: boolean;
  recovered_days: number;
  parent_approved: "pending" | "accepted" | "rejected";
  principal_approved: "pending" | "accepted" | "rejected";
  lea_approved: "pending" | "accepted" | "rejected";
  principal_suggestion: boolean;
  lea_suggestion: boolean;
  teacher_required: boolean;
  created_at: string;
  created_by: string;
  grade: number;
}

const LeaveDetailsModal: React.FC<LeaveDetailsModalProps> = ({
  leaveId,
  open,
  onClose,
}) => {
  // Only fetch data when modal is open and leaveId exists
  const shouldFetch = !!leaveId && open;

  const {
    data: leaveDetails,
    isLoading,
    isError,
  } = useGetApi(
    shouldFetch
      ? leaveApis(leaveId).getLeaveDetails
      : {
          queryKey: ["Get-leave-details-disabled"],
          queryFn: () => Promise.resolve(null),
        },
  ) as {
    data: LeaveDetailsResponse | undefined;
    isLoading: boolean;
    isError: boolean;
  };

  const formatReason = (reason: string | null) => {
    if (!reason) return "N/A";
    return reason
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formatRecoveryMethod = (method: string | null) => {
    if (!method) return "N/A";
    return method
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const InfoCard = ({
    icon,
    title,
    children,
  }: {
    icon: React.ReactNode;
    title: string;
    children: React.ReactNode;
  }) => (
    <div className="border p-2 border-slate-300 rounded-xl my-4">
      <p className="mb-2 ml-2 font-semibold text-slate-700 flex items-center gap-2">
        {icon}
        {title}
      </p>
      <div className="px-2">{children}</div>
    </div>
  );

  const InfoRow = ({
    label,
    value,
  }: {
    label: string;
    value: React.ReactNode;
  }) => (
    <div className="flex justify-between items-center py-2.5">
      <span className="text-sm text-slate-600">{label}</span>
      <span className="text-sm text-slate-900 font-medium text-right ml-4">
        {value}
      </span>
    </div>
  );

  const PersonCard = ({ name, email }: { name: string; email: string }) => (
    <div className="bg-slate-50 rounded-lg p-3 mb-2 border border-slate-200">
      <p className="text-sm font-medium text-slate-900">{name || "N/A"}</p>
      <p className="text-xs text-slate-500 mt-1">{email}</p>
    </div>
  );

  if (!leaveId) {
    return (
      <Drawer
        width={500}
        title="Leave Details"
        open={open}
        onClose={onClose}
        footer={null}
      >
        <Empty description="No leave selected" />
      </Drawer>
    );
  }

  return (
    <Drawer
      width={500}
      title="Leave Request Details"
      open={open}
      onClose={onClose}
      footer={null}
      destroyOnClose
    >
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      ) : isError || !leaveDetails || !leaveDetails.student ? (
        <Empty description="Failed to load leave details" />
      ) : (
        <div>
          {/* Use the same card component from Leave Tracker */}
          <LeaveDetailsCard
            record={{
              ...leaveDetails,
              dayofabsence: leaveDetails.total_days,
            }}
          />

          {/* Leave Dates & Details */}
          <InfoCard icon={<IconCalendar size={18} />} title="Leave Information">
            <InfoRow
              label="Start Date"
              value={dayjs(leaveDetails.start_date).format("MMM DD, YYYY")}
            />
            <InfoRow
              label="End Date"
              value={dayjs(leaveDetails.end_date).format("MMM DD, YYYY")}
            />
            <InfoRow label="Total Days" value={leaveDetails.total_days} />
            {leaveDetails.selected_dates && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                <p className="text-sm text-slate-600 mb-2">Selected Dates:</p>
                <div className="flex flex-wrap gap-2">
                  {leaveDetails.selected_dates
                    .split(",")
                    .map((date: string) => (
                      <Tag key={date} color="blue">
                        {dayjs(date).format("MMM DD")}
                      </Tag>
                    ))}
                </div>
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-slate-200">
              <InfoRow
                label="Requested On"
                value={dayjs(leaveDetails.requested_date).format(
                  "MMM DD, YYYY",
                )}
              />
              <InfoRow
                label="Reason"
                value={formatReason(leaveDetails.reason)}
              />
              {leaveDetails.other && (
                <InfoRow label="Other Details" value={leaveDetails.other} />
              )}
            </div>
          </InfoCard>

          {/* Recovery Information */}
          <InfoCard
            icon={<IconFileText size={18} />}
            title="Recovery Information"
          >
            <InfoRow
              label="Recovery Method"
              value={formatRecoveryMethod(leaveDetails.recovery_method)}
            />
            <InfoRow
              label="IS Start Date"
              value={dayjs(leaveDetails.is_start_date).format("MMM DD, YYYY")}
            />
            <InfoRow
              label="IS End Date"
              value={dayjs(leaveDetails.is_end_date).format("MMM DD, YYYY")}
            />
            <div className="mt-3 pt-3 border-t border-slate-200">
              {leaveDetails.is_recovered && (
                <div className="mt-2">
                  <Badge status="success" text="Fully Recovered" />
                </div>
              )}
            </div>
          </InfoCard>

          {/* Approval Status */}
          {/* <InfoCard
            icon={<IconClipboardCheck size={18} />}
            title="Approval Status"
          >
            <InfoRow
              label="Parent Approval"
              value={getApprovalBadge(leaveDetails.parent_approved)}
            />
            <InfoRow
              label="Principal Approval"
              value={getApprovalBadge(leaveDetails.principal_approved)}
            />
            {leaveDetails.principal_suggestion && (
              <div className="ml-4 mt-1">
                <Tag color="orange" className="text-xs">
                  💡 Suggestions Available
                </Tag>
              </div>
            )}
            <InfoRow
              label="LEA Approval"
              value={getApprovalBadge(leaveDetails.lea_approved)}
            />
            {leaveDetails.lea_suggestion && (
              <div className="ml-4 mt-1">
                <Tag color="orange" className="text-xs">
                  💡 Suggestions Available
                </Tag>
              </div>
            )}
          </InfoCard> */}

          {/* Guardian Information */}
          {leaveDetails.parent && leaveDetails.parent.length > 0 && (
            <InfoCard icon={<IconUsers size={20} />} title="Parent/Guardian">
              {leaveDetails.parent.map(
                (parent: { id: number; name: string; email: string }) => (
                  <PersonCard
                    key={parent.id}
                    name={parent.name}
                    email={parent.email}
                  />
                ),
              )}
              <div className="mt-3">
                {leaveDetails.relation && (
                  <InfoRow label="Relation" value={leaveDetails.relation} />
                )}
                {leaveDetails.guardian_confirmed && (
                  <InfoRow
                    label="Confirmed Via"
                    value={formatReason(leaveDetails.guardian_confirmed)}
                  />
                )}
              </div>
            </InfoCard>
          )}

          {/* Teachers */}
          {leaveDetails.teacher && leaveDetails.teacher.length > 0 && (
            <InfoCard icon={<IconUsers size={20} />} title="Assigned Teachers">
              {leaveDetails.teacher.map(
                (teacher: { id: number; name: string; email: string }) => (
                  <PersonCard
                    key={teacher.id}
                    name={teacher.name}
                    email={teacher.email}
                  />
                ),
              )}
            </InfoCard>
          )}

          {/* Attendance Coordinators */}
          {leaveDetails.attendace_coordinator &&
            leaveDetails.attendace_coordinator.length > 0 && (
              <InfoCard
                icon={<IconUsers size={20} />}
                title="Attendance Coordinators"
              >
                {leaveDetails.attendace_coordinator.map(
                  (coordinator: {
                    id: number;
                    name: string;
                    email: string;
                  }) => (
                    <PersonCard
                      key={coordinator.id}
                      name={coordinator.name}
                      email={coordinator.email}
                    />
                  ),
                )}
              </InfoCard>
            )}

          {/* Additional Information */}
          <InfoCard icon={<IconFileText size={20} />} title="Additional Info">
            <InfoRow
              label="Terms Accepted"
              value={
                leaveDetails.terms_accepted ? (
                  <Badge status="success" text="Yes" />
                ) : (
                  <Badge status="error" text="No" />
                )
              }
            />
            <InfoRow
              label="Teacher Required"
              value={
                leaveDetails.teacher_required ? (
                  <Badge status="success" text="Yes" />
                ) : (
                  <Badge status="default" text="No" />
                )
              }
            />
            <InfoRow label="Created By" value={leaveDetails.created_by} />
            <InfoRow
              label="Created At"
              value={dayjs(leaveDetails.created_at).format(
                "MMM DD, YYYY HH:mm",
              )}
            />
          </InfoCard>
          {/* School & District Information */}
          <InfoCard icon={<IconSchool size={18} />} title="School & District">
            <InfoRow
              label="School"
              value={leaveDetails.school?.name || "N/A"}
            />
            <InfoRow
              label="District"
              value={leaveDetails.district_admin?.name || "N/A"}
            />
          </InfoCard>
        </div>
      )}
    </Drawer>
  );
};

export default LeaveDetailsModal;
